import { useCallback, useEffect, useRef, useState } from "react";
import useVideoContext from "../useVideoContext/useVideoContext";
import { RoomEvent, Track, TrackEvent } from "livekit-client";

const desiredPausedByTrack = new WeakMap();

export default function useLocalAudioToggle() {
  const { room, localTracks } = useVideoContext();
  const audioTrack = localTracks.find((track) => track?.source.includes(Track.Source.Microphone));
  const [isMuted, setIsMuted] = useState(audioTrack ? audioTrack.isMuted : false);

  // Derive mute state from the track itself: the mic can be muted/unmuted by
  // more than one caller (the mic button, auto-mute during okobot speech), and
  // a per-mount useState copy goes stale for every mount that didn't toggle.
  useEffect(() => {
    if (!audioTrack) return;
    const sync = () => setIsMuted(audioTrack.isMuted);
    sync();
    audioTrack.on("muted", sync);
    audioTrack.on("unmuted", sync);
    return () => {
      audioTrack.off("muted", sync);
      audioTrack.off("unmuted", sync);
    };
  }, [audioTrack]);

  const toggleAudioEnabled = useCallback(() => {
    if (audioTrack) {
      if (audioTrack.isMuted) {
        audioTrack.unmute();
      } else {
        audioTrack.mute();
      }
    }
  }, [audioTrack]);

  // A "soft" mute: stop sending audio while leaving the capture running. A
  // real mute disables the capture track, which makes iOS switch a Bluetooth
  // headset between its call and music profiles — a multi-second gap that
  // swallows the start of whatever OKO is saying. Pausing the upstream keeps
  // the audio session untouched, so the headset never switches.
  const upstreamPausedRef = useRef(false);
  const applyUpstream = useCallback(() => {
    if (!audioTrack) return;
    const shouldPause = desiredPausedByTrack.get(audioTrack) ?? upstreamPausedRef.current;
    if (Boolean(audioTrack.isUpstreamPaused) === shouldPause) return;
    const op = shouldPause ? audioTrack.pauseUpstream() : audioTrack.resumeUpstream();
    op?.catch?.(() => {});
  }, [audioTrack]);

  const setUpstreamPaused = useCallback((paused) => {
    upstreamPausedRef.current = paused;
    if (audioTrack) {
      desiredPausedByTrack.set(audioTrack, paused);
    }
    applyUpstream();
  }, [audioTrack, applyUpstream]);

  // LiveKit resumes the upstream itself after a device restart, republishes
  // the track on reconnect, and on iOS resumes it again when an interruption
  // (a video, Siri, a call) hands the mic back; all would leak audio
  // mid-pause, so re-apply.
  useEffect(() => {
    if (!audioTrack) return;
    if (upstreamPausedRef.current) {
      desiredPausedByTrack.set(audioTrack, true);
    }
    applyUpstream();
    audioTrack.on(TrackEvent.Restarted, applyUpstream);
    audioTrack.on(TrackEvent.UpstreamResumed, applyUpstream);
    room?.on?.(RoomEvent.LocalTrackPublished, applyUpstream);
    return () => {
      audioTrack.off(TrackEvent.Restarted, applyUpstream);
      audioTrack.off(TrackEvent.UpstreamResumed, applyUpstream);
      room?.off?.(RoomEvent.LocalTrackPublished, applyUpstream);
    };
  }, [audioTrack, room, applyUpstream]);

  return { isMuted, toggleAudioEnabled, setUpstreamPaused };
}
